import React, { FC, Fragment, MouseEvent, useEffect, useState } from 'react';
import { formatDate, formatPatientName } from 'utils/format';
import { TableCell, TableRow, Box, IconButton, Typography } from '@mui/material';
import { Order } from 'models/order';
import RestorationTypes from 'components/orders/shared/restoration-types.component';
import DeliveryDetails from 'components/orders/shared/delivery-details.component';
import OrderStatusChip from 'components/orders/shared/order-status-chip.component';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { OrderItemContext } from 'models/order-item-events';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LabFlagButton from 'components/shared/lab-flag-button/lab-flag-button.component';
import { OrderTag } from 'models/tags';
import OrderListTagComponent from 'components/shared/order-list-tag/order-list-tag.component';
import OrderCheckBox from 'components/shared/order-check-box/order-check-box.component';
import { isSupplier } from 'utils/client-settings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type ExternalOrderItemProps = {
    order: Order;
    hidePatientDetails?: boolean;
    onOrderContextMenu: (orderContext: OrderItemContext) => void;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    orderIndex: number;
    isAtLimit?: boolean;
    isChecked?: boolean;
};
const ExternalOrderItem: FC<ExternalOrderItemProps> = ({
    order,
    onOrderContextMenu,
    hidePatientDetails,
    batchActionSelected,
    onHandleCheckedOrders,
    orderIndex,
    isAtLimit,
    isChecked,
}: ExternalOrderItemProps) => {
    const navigate = useNavigate();
    const { t: translate } = useTranslation(['orders']);
    const currentOrganization = currentOrgVar();
    const [orderTags, setOrderTags] = useState<OrderTag[]>();

    useEffect(() => {
        setOrderTags(order.orderTags);
    }, [order.orderTags]);

    const navigateToOrderDetails = () => {
        navigate(`/order-details/${order._id}/main-view`);
    };

    const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onOrderContextMenu({ order: order, mouseX: event.clientX, mouseY: event.clientY });
    };

    const updateOrderTag = (tags: OrderTag[]) => {
        setOrderTags(tags);
    };

    return (
        <TableRow
            sx={{
                cursor: 'pointer',
                borderLeft: `4px solid ${orderTags?.length === 1 ? orderTags[0].color : '#fff'}`,
            }}
            hover
            onClick={() => {
                navigateToOrderDetails();
            }}
            onContextMenu={handleContextMenu}
        >
            {batchActionSelected ? (
                <TableCell sx={{ padding: 0 }}>
                    <OrderCheckBox
                        order={order}
                        isAtLimit={isAtLimit}
                        checked={isChecked ?? false}
                        onHandleCheckedOrders={
                            onHandleCheckedOrders &&
                            ((checkedOrder, checked) => onHandleCheckedOrders(checkedOrder, checked, orderIndex ?? 0))
                        }
                    />
                </TableCell>
            ) : (
                <TableCell sx={{ padding: 0 }}>
                    {isSupplier(currentOrganization._id, order.supplier._id) ? (
                        <LabFlagButton order={order} />
                    ) : undefined}
                </TableCell>
            )}
            <TableCell sx={{ maxWidth: '10vw' }}>
                <OrderStatusChip status={order.status} />
                {orderTags ? (
                    <OrderListTagComponent updateOrderTag={updateOrderTag} orderId={order._id} orderTags={orderTags} />
                ) : undefined}
            </TableCell>
            <TableCell>
                <Box>{order.orderId}</Box>
                {order.internalOrderId ? <Typography variant="caption">{order.internalOrderId}</Typography> : undefined}
            </TableCell>
            {!hidePatientDetails ? (
                <TableCell sx={{ maxWidth: 200 }}>
                    <Box>{formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}</Box>
                </TableCell>
            ) : undefined}
            <TableCell sx={{ maxWidth: 200 }}>
                <RestorationTypes sampleType={order.deliveryDetails.sampleType} types={order.restorationTypes} />
            </TableCell>
            <TableCell sx={{ minWidth: 125, maxWidth: 140 }}>
                {order.workFlow?.currentStep && order.workFlow?.currentStep.dateTime ? (
                    <Fragment>
                        <Box>{order.workFlow.currentStep.name}</Box>
                        <Typography variant="caption">
                            {formatDate(new Date(order.workFlow.currentStep.dateTime), 'datetime')}
                        </Typography>
                    </Fragment>
                ) : (
                    <Box>-</Box>
                )}
            </TableCell>
            <TableCell sx={{ width: 115 }}>
                <DeliveryDetails details={order.deliveryDetails} />
            </TableCell>
            <TableCell sx={{ maxWidth: '30px', paddingLeft: 0 }}>
                <IconButton size="small" onClick={handleContextMenu}>
                    <MoreVertIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default ExternalOrderItem;
